import { SharedProgrammingService } from './shared-programming.service';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Broker } from '@c4/core';

import { DeviceCategoryService } from './device-category.service';
import { SET_WORKING, SELECT_EVENT_CATEGORY, SELECT_EVENT_DEVICE, SELECT_EVENT, CLEAR_EVENT, CLEAR_EVENT_CATEGORY, CLEAR_EVENT_DEVICE,
  SET_EVENT_DEVICE_SEARCH, CLEAR_ERRORS, SET_EVENTS, SET_EVENT_DEVICES } from './../../advanced/services/advanced-programming.service';

import { CodeItemService } from './codeitem.service';
import { ProgrammingUtilsService } from './utils.service';

const CLIPBOARD_CUT: string = 'CLIPBOARD_CUT';
const CLIPBOARD_COPY: string = 'CLIPBOARD_COPY';

@Injectable()
export class ProgrammingEventService {

  event: Observable<any>;

  private _copiedItem: any;
  private _operation: string;

  constructor(
    private store: Store<any>,
    private broker: Broker,
    private codeitemService: CodeItemService,
    private deviceCategory: DeviceCategoryService,
    private shared: SharedProgrammingService,
    private utils: ProgrammingUtilsService
  ) {
    this.event = this.store.select(state => state.advancedProgramming.event);
  }

  init() {
  }

  selectCategory(category: string) {
    this.store.dispatch({ type: SELECT_EVENT_CATEGORY, payload: category });
    this.getCategoryDevices(category);
  }

  selectDevice(device: any) {
    this.store.dispatch({ type: SELECT_EVENT_DEVICE, payload: device });
    if (device) {
      this.getDeviceEvents(device.id);
    }
  }

  selectEvent(evt: any): Promise<any> {
    this.shared.ready(false);
    console.log('evt.svc: getting event defn for ', evt);
    if (evt) {
      return this.broker.call({
        path: '/api/v1/items/' + evt.deviceId + '/events/' + evt.eventId
      }).then(
        res => {
          this.shared.ready(true);
          console.log('autevtsvc: got event', res[0]);
          this.store.dispatch({ type: SELECT_EVENT, payload: res[0] });
        },
        err => {
          this.shared.ready(true);
          console.error('autevtsvc: error getting full event defn', err);
        });
    } else {
      return new Promise((resolve, reject) => {
        reject({ err: 'prg-evt.svc: invalid input, no event given' });
      });
    }
  }

  clear() {
    this.store.dispatch({ type: CLEAR_EVENT });
  }

  clearCategory() {
    this.store.dispatch({ type: CLEAR_EVENT_CATEGORY });
  }

  clearDevice() {
    this.store.dispatch({ type: CLEAR_EVENT_DEVICE });
  }

  retrieve(deviceId: number, eventId: number) {
    console.log('events: retrieve: ', deviceId, eventId);
    this.clearError();
    this.shared.ready(false);

    let requests = [];

    requests.push(this.broker.call({
      path: '/api/v1/items/' + deviceId + '/events/' + eventId
    }));

    requests.push(this.broker.call({
      path: '/api/v1/items/' + deviceId
    }));

    return Promise.all(requests).then(
      res => {
        let device = res[1][0], event = res[0][0];

        if (device.type === 6 && device.proxy) {
          let query = { parentId: device.id };
          this.broker.call({
            path: '/api/v1/items?query=' + JSON.stringify(query)
          }).then(
            proxies => {
              device.proxies = proxies;
              for (let i = 0; i < proxies.length; i++) {
                let p = proxies[i];
                if (p.proxy === device.proxy) {
                  device.defaultProxy = p;
                  break;
                }
              }

              this.store.dispatch({
                type: SELECT_EVENT,
                payload: { device: device, event: event }
              });

              this.selectEvent(event);
              this.shared.ready(true);
            },
            err => {
              this.clear();
              this.shared.ready(true);
            });
        } else {
          this.store.dispatch({
            type: SELECT_EVENT,
            payload: { device: device, event: event }
          });
          this.shared.ready(true);
        }
      },
      err => {
        console.log('autoevtsvc: error getting automation event', err);
        this.clear();
        this.shared.ready(true);
      });
  }

  refresh() {
    this.clearError();

    this.store.select(state => state.event).take(1).subscribe(
      (automation: any) => {
        if (automation && automation.event) {
          this.retrieve(automation.event.deviceId, automation.event.eventId);
        }
      });
  }

  deleteCodeItem(id: number): Promise<any> {
    this.clearError();
    this.shared.ready(false);

    let automation = this.utils.getValue(this.store, s => s.event);
    try {
      return this.broker.call({
        method: 'DELETE',
        path: '/api/v1/items/' + automation.device.id +
        '/events/' + automation.event.eventId + '/' + id
      }).then(
        res => {
          console.log('autevtsvc: code item deleted');
          this.refresh();
        },
        err => {
          this.shared.error(err);
          console.error('autevtsvc: error deleting code item', err);
        });
    } catch (e) {
      console.error('autevtsvc: exception deleting code item', e);
      this.shared.error(e);
    } finally {
      this.shared.ready(true);
    }
  }

  deleteAllCodeItems() {
    this.clearError();

    let automation = this.utils.getValue(this.store, s => s.event);
    if (automation) {
      if (automation.event.codeItems && automation.event.codeItems.length > 0) {
        this.shared.ready(false);

        try {
          let requests = [];
          automation.event.codeItems.forEach((ci: any) => {
            requests.push(this.broker.call({
              method: 'DELETE',
              path: '/api/v1/items/' + automation.device.id +
              '/events/' + automation.event.eventId + '/' + ci.codeitemId
            }));
          });

          Promise.all(requests).then(
            res => {
              console.log('autevtsvc: all code item deleted');
              this.refresh();
            },
            err => {
              console.error('autevtsvc: error deleting all code items', err);
              this.shared.error(err);
            });
        } catch (e) {
          console.error('autevtsvc: exception deleting all code items', e);
          this.shared.error(e);
        } finally {
          this.shared.ready(true);
        }
      }
    }
  }

  cutItem(codeitem: any) {
    this.clearClipboard();
    try {
      this.shared.ready(false);
      this._operation = CLIPBOARD_CUT;
      this._copiedItem = Object.assign({}, codeitem, { codeitemId: 0, parentCodeItemId: 0, beforeCodeItemId: 0, creatorState: {} });
      this.deleteCodeItem(codeitem.codeitemId).then(
        res => {
          console.log('autoevt.svc: codeitem cut', codeitem.codeitemId);
          this.refresh();
        },
        err => {
          console.error('autoevt.svc: codeitem cut error', err);
          this.shared.error(err);
        });
    } catch (e) {
      console.error('autoevt.svc: codeitem cut exception', e);
      this.shared.error(e);
    } finally {
      this.shared.ready(true);
    }
  }

  copyItem(codeitem: any) {
    this.clearClipboard();
    this._operation = CLIPBOARD_COPY;
    this._copiedItem = Object.assign({}, codeitem, { codeitemId: 0, parentCodeItemId: 0, beforeCodeItemId: 0, creatorState: {} });
    this._copiedItem.codeitemId = 0;
  }

  pasteItem(parent: any) {
    this.clearError();
    try {
      this.shared.ready(false);
      let codeitem = Object.assign({}, this._copiedItem);
      codeitem.parentCodeItemId = (parent) ? parent.codeitemId : 0;
      codeitem.beforeCodeItemId = 0;

      let automation = this.utils.getValue(this.store, s => s.event);
      this.codeitemService.save();
      if (this._operation === CLIPBOARD_CUT) {
        this._operation = CLIPBOARD_COPY;
        this._copiedItem.codeitemId = 0;
      }

      this.refresh();
    } catch (e) {
      console.error('autoevt.svc: exception pasting item', e);
      this.shared.error(e);
    } finally {
      this.shared.ready(true);
    }
  }

  setDeviceSearchText(text: string) {
    this.store.dispatch({ type: SET_EVENT_DEVICE_SEARCH, payload: text });
  }

  private addParams(params: Array<any>): Array<any> {
    let minparams: Array<any> = [];

    params.forEach((p) => {
      minparams.push({
        name: p.name,
        value: p.value
      });
    });

    return minparams;
  }

  moveCodeItem(event: any, movedId: number, droppedOnId: number, droppedBeforeId: number): Promise<any> {

    // find the items we know about
    var movedItem = this.findCodeItem(movedId, event);
    var droppedOnItem = this.findCodeItem(droppedOnId, event);
    // beforeItem might be undefined
    var beforeItem = this.findCodeItem(droppedBeforeId, event);

    let parentId: number = droppedOnItem.codeitemId || 0;
    let beforeId: number = (beforeItem) ? beforeItem.codeitemId || 0 : 0;

    // determine the actual parent of the dropped item

    // if the dropped on item is not a conditional or loop, then the new parent is the parent of the dropped-on element
    // which might be the automation event itself.
    if (droppedOnItem.type === 1) {
      parentId = droppedOnItem.parentCodeItemId || 0;
    }

    // if the dropped on item and dropped before item are the same, let the dropped item inherit hte same parent
    // NOTE: beforeId should be correct in this case
    if (droppedBeforeId === droppedOnId) {
      let parent = this.findParent(event, droppedOnItem);
      if (parent) {
        parentId = parent.codeitemId;
      }
    }

    // determine the actual before item

    // if the item was dropped directly on a conditional or loop, the item should be added to the end of the list of
    // codeitems for that parent, even though the beforeId and parentId will be equal
    if (droppedOnItem.type > 1) {
      beforeId = 0;
    }

    let path = '/api/v1/items/' + event.deviceId;
    path += ('/events/' + event.eventId);
    path += ('/' + movedItem.codeitemId);

    var data: any = {
      'beforeCodeItemId': beforeId || 0,
      'parentCodeItemId': parentId || 0
    };

    return this.broker.call({
      method: 'POST',
      path: path,
      data: data
    });
  }

  private clearError() {
    this.store.dispatch({ type: CLEAR_ERRORS });
  }

  private findCodeItem(codeitemId: number, parent: any): any {
    var found: any;

    if (codeitemId > 0) {
      if (parent && parent.codeitemId === codeitemId) {
        return parent;
      } else {
        if (parent.codeItems) {
          for (var index = 0; index < parent.codeItems.length; index++) {
            var child = parent.codeItems[index];
            found = this.findCodeItem(codeitemId, child);
            if (found) {
              break;
            }
          }
        }
      }
    }

    return found;
  }

  private findParent(currentItem: any, targetItem: any): any {
    var found = null;

    if (currentItem.codeItems) {
      for (var index = 0; index < currentItem.codeItems.length; index++) {
        var childItem = currentItem.codeItems[index];
        if (childItem.codeitemId === targetItem.codeitemId) {
          // NOTE we're looking for the parent of the target item, not the target item itself
          found = currentItem;
          break;
        } else {
          var tmp = this.findParent(childItem, targetItem);
          if (tmp) {
            found = tmp;
            break;
          }
        }
      }
    }

    return found;
  }

  private getDeviceEvents(deviceId: any): Promise<any> {
    console.log('getting events for device: ', deviceId);
    this.shared.ready(false);

    return this.broker.call({
      path: '/api/v1/items/' + deviceId + '/events?allevents=true'
    }).then(
      (events: Array<any>) => {
        // console.log('got events for device', events);
        this.shared.ready(true);
        events.sort(this.compareEvents);
        this.store.dispatch({ type: SET_EVENTS, payload: events });
        this.store.dispatch({ type: SET_WORKING, payload: false });
      },
      (err: any) => {
        this.shared.ready(true);
        console.log('autevtsvc: get device events error', err);
        this.store.dispatch({ type: SET_EVENTS, payload: [] });
        this.store.dispatch({ type: SET_WORKING, payload: false });
      });
  }

  private compareEvents(a: any, b: any): number {
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  }

  private compareDevices(a: any, b: any) {
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  }

  private resolveDevices(items: Array<any>): Array<any> {
    let devices = [];
    let devMap = {};
    let protocols = {};

    items.forEach((item: any) => {
      switch (item.type) {
        case 7:
          if (item.protocolId) {
            let prot = protocols[item.protocolId];
            if (prot) {
              item.protocolItem = prot;
            }
          }

          devMap[item.id] = item;
          break;
        case 6:
          protocols[item.id] = item;
          break;
        default:
          console.log('evt.svc: ignoring item', item);
          break;
      }
    });

    // now convert to an iterable
    Object.keys(devMap).forEach(key => {
      devices.push(devMap[key]);
    });

    return devices;
  }

  private moveChildren(parent: any, codeitems: Array<any>): Promise<any> {
    return new Promise((resolve, reject) => {

    });
  }

  private clearClipboard() {
    this._copiedItem = undefined;
    this._operation = undefined;
  }

  getCategoryDevices(cat: string) {
    this.broker.call({
      path: '/api/v1/categories/' + cat
    }).then(res => {
      this.store.dispatch({ type: SET_EVENT_DEVICES, payload: this.resolveDevices(res) });
    });
  }


  getCategories(): Promise<any> {
    return this.broker.call({
      path: '/api/v1/categories'
    });
  }

  private findIndexOf(parent: any, child: any): number {
    var idx = 0;

    if (parent.codeItems) {
      for (var index = 0; index < parent.codeItems.length; index++) {
        var tmp = parent.codeItems[index];
        if (tmp.codeitemId === child.codeitemId) {
          idx = index;
          break;
        }
      }
    }

    return idx;
  }

  private hasParams(codeitem: any): boolean {
    return (
      codeitem &&
      codeitem.params &&
      typeof codeitem.params === 'object' &&
      codeitem.params.length &&
      codeitem.params.length > 0
    );
  }
}



// WEBPACK FOOTER //
// ./~/@c4/programming/src/app/common/services/event.service.ts